import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';

import { ApiEndpoints, RequestType } from 'src/utils/enums';
import { AXIOS_CONFIG, ERROR_MESSAGES } from 'src/utils/constants';
import { Interest } from 'src/models/Interest';
import { getNormalizedApiEndpoint } from 'src/utils/utils';
import { Device } from 'src/models/Device';
import { Profile } from 'src/models/Profile';
import { PromoBanner } from 'src/models/PromoBanner';
import { Challenges } from 'src/models/Challenges';
import { CardActivities } from 'src/models/CardActivities/CardActivities';
import { handleApiError } from 'src/errors';
import { Rewards } from 'src/models/rewards/Rewards';
import { DailyHabit } from 'src/models/dailyHabit/DailyHabit';
import { HomeWidgetText } from 'src/models/homeWidgetText/HomeWidgetText';
import { AuthUser } from 'src/models/AuthUser';
import { HealthTracker } from 'src/models/healthTrackers/HealthTracker';
import { HealthGoal } from 'src/models/health-goals/HealthGoal';

type AccessTokenGetter = () => Promise<string | null | undefined>;
type HttpMethod = 'get' | 'post' | 'put' | 'delete' | 'patch';

export interface ApiServiceInterface {
  getInterests(): Promise<Interest[]>
  getDevices(): Promise<Device[]>
  getProfile(): Promise<Profile>
  getPromoBanner(): Promise<PromoBanner>
  getRewards(): Promise<Rewards>
  getActivities(): Promise<Challenges[]>
  getCardActivities(): Promise<CardActivities>
  getHomeWidgetText(): Promise<HomeWidgetText>
  signInWithKey(accessKey: string): Promise<AuthUser>
  getHealthGoals(): Promise<HealthGoal>
}

export default class ApiService implements ApiServiceInterface {
  private _accessTokenGetter: AccessTokenGetter;

  public get accessTokenGetter(): AccessTokenGetter {
    return this._accessTokenGetter;
  }

  public set accessTokenGetter(value: AccessTokenGetter) {
    this._accessTokenGetter = value;
  }

  constructor(accessTokenGetter: AccessTokenGetter) {
    this._accessTokenGetter = accessTokenGetter;
  }

  private createRetryableAxios(apiPath: string): AxiosInstance {
    const instance = axios.create();
    axiosRetry(instance, {
      retries: AXIOS_CONFIG.MAX_RETRIES,
      retryDelay: (retryCount) => {
        return retryCount * AXIOS_CONFIG.RETRY_DELAY_IN_MILLISECONDS;
      },
      onRetry: () => {
        // TODO: replace with logger
        // eslint-disable-next-line no-console
        console.log(`Retry the request to: ${apiPath}`);
      },
    });

    return instance;
  }

  private async getDefaultHeaders(authenticationRequired: boolean): Promise<Record<string, string>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authenticationRequired) {
      const token = await this.accessTokenGetter();

      if (!token) throw new Error(ERROR_MESSAGES.invalidAccessToken);
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private async buildAxiosConfig(
    apiFunctionPath: string,
    requestType: RequestType,
    params?: Record<string, unknown>,
    payload?: Record<string, unknown>,
    headerOptions?: Record<string, string>,
    authenticationRequired: boolean = true,
  ): Promise<AxiosRequestConfig> {
    const headers = await this.getDefaultHeaders(authenticationRequired);
    const apiPath = getNormalizedApiEndpoint(apiFunctionPath);
    const axiosConfig: AxiosRequestConfig = {
      method: requestType.toLowerCase() as HttpMethod,
      url: apiPath,
      params,
      data: payload,
      headers: { ...headers, ...headerOptions },
      timeout: AXIOS_CONFIG.TIMEOUT_IN_MILLISECONDS,
    };

    return axiosConfig;
  }

  private async processRequest<T>(config: AxiosRequestConfig, apiPath: string): Promise<T> {
    const axiosInstance = this.createRetryableAxios(apiPath);
    const response = await axiosInstance.request(config);

    return response.data;
  }

  private async callApi<T>(
    apiFunctionPath: string,
    requestType: RequestType,
    params?: Record<string, unknown>,
    payload?: Record<string, unknown>,
    headerOptions?: Record<string, string>,
    authenticationRequired: boolean = true,
  ): Promise<T> {
    const axiosConfig = await this.buildAxiosConfig(
      apiFunctionPath,
      requestType,
      params,
      payload,
      headerOptions,
      authenticationRequired,
    );

    if (!axiosConfig.url) throw new Error(ERROR_MESSAGES.invalidApiEndpoint);

    return await this.processRequest<T>(axiosConfig, axiosConfig.url);
  }

  public getInterests = async (): Promise<Interest[]> => {
    try {
      return await this.callApi<Interest[]>(ApiEndpoints.INTERESTS, RequestType.GET);
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.INTERESTS);
    }
  };

  public getDevices = async (): Promise<Device[]> => {
    try {
      return await this.callApi<Device[]>(ApiEndpoints.DEVICES, RequestType.GET);
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.DEVICES);
    }
  };

  public getProfile = async (): Promise<Profile> => {
    try {
      return await this.callApi<Profile>(ApiEndpoints.PROFILE, RequestType.GET);
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.PROFILE);
    }
  };

  public getPromoBanner = async (): Promise<PromoBanner> => {
    try {
      return await this.callApi<PromoBanner>(ApiEndpoints.PROMO_BANNER, RequestType.GET);
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.PROMO_BANNER);
    }
  };

  public getRewards = async (): Promise<Rewards> => {
    try {
      return await this.callApi<Rewards>(ApiEndpoints.REWARDS, RequestType.GET);
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.REWARDS);
    }
  };

  public getActivities = async (): Promise<Challenges[]> => {
    try {
      return await this.callApi<Challenges[]>(ApiEndpoints.CHALLENGES, RequestType.GET);
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.CHALLENGES);
    }
  };

  public getCardActivities = async (): Promise<CardActivities> => {
    try {
      return await this.callApi<CardActivities>(ApiEndpoints.CARDS_ACTIVITIES, RequestType.GET);
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.CARDS_ACTIVITIES);
    }
  };

  public getDailyHabits = async (): Promise<DailyHabit> => {
    try {
      return await this.callApi<DailyHabit>(ApiEndpoints.DAILY_HABITS, RequestType.GET);
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.DAILY_HABITS);
    }
  };

  public getHomeWidgetText = async (): Promise<HomeWidgetText> => {
    try {
      return await this.callApi<HomeWidgetText>(ApiEndpoints.HOME_WIDGET_TEXT, RequestType.GET);
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.HOME_WIDGET_TEXT);
    }
  };

  // TODO: accessKey to use later
  public signInWithKey = async (accessKey: string): Promise<AuthUser> => {
    try {
      return await this.callApi<AuthUser>(
        ApiEndpoints.SIGN_IN_WITH_KEY,
        RequestType.POST,
        undefined,
        { accessKey },
        undefined,
        false,
      );
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.SIGN_IN_WITH_KEY);
    }
  };

  public getHealthTrackers = async (): Promise<HealthTracker[]> => {
    try {
      return await this.callApi<HealthTracker[]>(ApiEndpoints.HEALTH_TRACKERS, RequestType.GET);
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.HEALTH_TRACKERS);
    }
  };

  public getHealthGoals = async (): Promise<HealthGoal> => {
    try {
      return await this.callApi<HealthGoal>(ApiEndpoints.HEALTH_GOALS, RequestType.GET);
    }
    catch (error: unknown) {
      handleApiError(error, ApiEndpoints.HEALTH_GOALS);
    }
  };
}
